import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowRight, Plus, BookOpen } from "lucide-react";
import { Header } from "@/client/components/layout/header";
import { Button } from "@/client/components/ui/button";
import { ProjectCard } from "@/client/components/projects/project-card";
import { ProjectForm } from "@/client/components/projects/project-form";
import { DeleteDialog } from "@/client/components/projects/delete-dialog";
import { InvitationsList } from "@/client/components/collaboration/invitations-list";
import {
  useStories,
  useCreateStory,
  useUpdateStory,
  useDeleteStory,
  lastStoryId,
} from "@/client/hooks/use-stories";
import { toast } from "sonner";
import { api, describeError, unwrap } from "@/client/lib/api";
import { forgetStory, type Story } from "@/client/db";

export const Route = createFileRoute("/")({
  component: Dashboard,
});

function Dashboard() {
  const { data: stories, isLoading } = useStories();
  const createStory = useCreateStory();
  const updateStory = useUpdateStory();
  const deleteStory = useDeleteStory();
  const navigate = useNavigate();
  const lastId = lastStoryId();
  const lastStory = stories.find((story) => story.id === lastId);

  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editingStory, setEditingStory] = useState<Story | null>(null);
  const [deletingStory, setDeletingStory] = useState<Story | null>(null);

  const handleCreate = () => {
    setEditingStory(null);
    setFormOpen(true);
  };

  const handleEdit = (story: Story) => {
    setEditingStory(story);
    setFormOpen(true);
  };

  const handleDelete = (story: Story) => {
    setDeletingStory(story);
    setDeleteOpen(true);
  };

  const handleSubmit = async (data: { name: string; synopsis?: string }) => {
    try {
      if (editingStory) {
        await updateStory.mutateAsync({ id: editingStory.id, data });
        setFormOpen(false);
      } else {
        // A new story opens straight away, on its board
        const story = await createStory.mutateAsync(data);
        setFormOpen(false);
        await navigate({ to: "/story/$storyId", params: { storyId: story.id } });
      }
    } catch {
      // The failure was reported; keep the form open
    }
  };

  const handleConfirmDelete = async () => {
    if (deletingStory) {
      try {
        await deleteStory.mutateAsync(deletingStory.id);
        setDeleteOpen(false);
        setDeletingStory(null);
      } catch {
        // The failure was reported
      }
    }
  };

  const handleLeave = async (story: Story) => {
    try {
      await unwrap(api.api.stories({ id: story.id }).leave.post());
      forgetStory(story.id);
      toast.success(`You left ${story.name}`);
    } catch (error) {
      toast.error("Couldn't leave the story", { description: describeError(error) });
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Your stories</h1>
            <p className="text-muted-foreground mt-1">
              Your Ren'Py visual novels, and the ones shared with you
            </p>
          </div>
          <Button onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-2" />
            New story
          </Button>
        </div>

        {lastStory && (
          <Button asChild variant="secondary" size="lg" className="mb-6 max-w-full justify-start">
            <Link to="/story/$storyId" params={{ storyId: lastStory.id }}>
              <span className="truncate">Continue {lastStory.name}</span>
              <ArrowRight />
            </Link>
          </Button>
        )}

        <InvitationsList />

        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-32 rounded-lg border bg-muted animate-pulse"
              />
            ))}
          </div>
        ) : stories && stories.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {stories.map((story) => (
              <ProjectCard
                key={story.id}
                story={story}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onLeave={handleLeave}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <BookOpen className="h-16 w-16 text-muted-foreground/50 mb-4" />
            <h2 className="text-xl font-semibold mb-2">No stories yet</h2>
            <p className="text-muted-foreground mb-6 max-w-md">
              Start your first visual novel. You can begin with a few loose
              ideas; the chapters come later.
            </p>
            <Button onClick={handleCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Start your first story
            </Button>
          </div>
        )}
      </main>

      <ProjectForm
        open={formOpen}
        onOpenChange={setFormOpen}
        story={editingStory}
        onSubmit={handleSubmit}
        isLoading={createStory.isPending || updateStory.isPending}
      />

      <DeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        story={deletingStory}
        onConfirm={handleConfirmDelete}
        isLoading={deleteStory.isPending}
      />
    </div>
  );
}
